import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { centroidFor } from "@/lib/zipCentroids";

// fix default marker icon path
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Creative icon glyphs — cycles based on DP index so every DP gets a distinct emoji.
const EP_GLYPHS = ["🚚", "✂️", "💇", "💅", "💄", "🎨", "🌟", "📦", "🏪", "🧴", "🪞", "🎯"];

// Warehouse is always the 🏭 icon with a dark navy background so it stands out.
const warehouseIcon = () => L.divIcon({
  className: "",
  html: `<div style="width:44px;height:44px;border-radius:50%;background:#0F172A;color:#fff;display:flex;align-items:center;justify-content:center;font-size:22px;border:3px solid #FFD700;box-shadow:0 4px 12px rgba(0,0,0,.4)">🏭</div>`,
  iconSize: [44, 44], iconAnchor: [22, 22],
});

const epIcon = (glyph, color) => L.divIcon({
  className: "",
  html: `<div style="width:36px;height:36px;border-radius:50%;background:${color};display:flex;align-items:center;justify-content:center;font-size:18px;border:3px solid white;box-shadow:0 0 0 2px ${color},0 2px 6px rgba(0,0,0,.35)">${glyph}</div>`,
  iconSize: [36, 36], iconAnchor: [18, 18],
});

const epPhotoIcon = (photoUrl, color) => L.divIcon({
  className: "",
  html: `<div style="width:42px;height:42px;border-radius:50%;background:${color};padding:2px;box-shadow:0 0 0 2px ${color},0 2px 6px rgba(0,0,0,.35);overflow:hidden">
           <img src="${photoUrl}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;border:2px solid white" alt="DP" onerror="this.parentElement.style.display='none'" />
         </div>`,
  iconSize: [42, 42], iconAnchor: [21, 21],
});

export default function FieldMap() {
  const [partners, setPartners] = useState([]);
  const [clients, setClients] = useState([]);

  useEffect(() => {
    const load = async () => {
      const [pp, cc] = await Promise.all([
        api.get("/endpoint-partners").then(r => r.data).catch(() => []),
        api.get("/clients").then(r => r.data).catch(() => []),
      ]);
      setPartners(pp);
      setClients(cc);
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const warehouse = useMemo(() => partners.find(p => p.id === "warehouse"), [partners]);
  const otherPartners = useMemo(() => partners.filter(p => p.id !== "warehouse"), [partners]);

  // Assign each DP a deterministic glyph based on their sort-order; admin's saved glyph/photo wins.
  const epsWithGlyph = useMemo(
    () => otherPartners.map((p, i) => ({
      ...p,
      glyph: p.icon_glyph || EP_GLYPHS[i % EP_GLYPHS.length],
    })),
    [otherPartners]
  );

  // Warehouse anchor: 8905 Ballard Ln, Clinton, MD 20735
  const center = warehouse?.lat && warehouse?.lng
    ? [warehouse.lat, warehouse.lng]
    : [38.7534, -76.8827];

  return (
    <div className="p-3 sm:p-6 lg:p-10 max-w-full">
      <header className="mb-6">
        <p className="label-eyebrow">Real-time field</p>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Field Map · Warehouse & Distributing Partner territories</h1>
        <p className="text-gray-500 mt-1">
          🏭 = Warehouse (8905 Ballard Ln, Clinton MD 20735). Each Distributing Partner has a unique icon and territory color, plotted at every zip in their coverage.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-3 p-0 overflow-hidden" data-testid="field-map">
          <div style={{ height: 620 }}>
            <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* Warehouse — always visible, cool icon */}
              {warehouse && (
                <Marker position={center} icon={warehouseIcon()} data-testid="warehouse-marker">
                  <Popup>
                    <div className="text-sm">
                      <div className="font-bold text-base">🏭 MPower! HQ</div>
                      <div className="text-gray-600 text-xs">Warehouse (Default)</div>
                      <div className="mt-1">{warehouse.address || "8905 Ballard Ln"}</div>
                      <div>{warehouse.city || "Clinton"}, {warehouse.state || "MD"} {warehouse.zip_code || "20735"}</div>
                    </div>
                  </Popup>
                </Marker>
              )}

              {/* Each DP: territory circles at every zip, plus a pin at each zip centroid */}
              {epsWithGlyph.map((p) => {
                const zips = (p.territory_zips || []).filter(Boolean);
                const centroids = zips.map(z => ({ zip: z, coord: centroidFor(z) })).filter(x => x.coord);
                // Fall back to DP's own lat/lng if we can't resolve any zip
                if (centroids.length === 0 && p.lat && p.lng) {
                  centroids.push({ zip: p.zip_code || "", coord: [p.lat, p.lng] });
                }
                return (
                  <React.Fragment key={p.id}>
                    {centroids.map(({ zip, coord }) => (
                      <React.Fragment key={`${p.id}-${zip}`}>
                        {/* Colored territory disc — overlaps welcome */}
                        <Circle center={coord} radius={2200}
                                pathOptions={{ color: p.color, fillColor: p.color, fillOpacity: 0.28, weight: 1 }} />
                        {/* DP pin at the zip centroid */}
                        <Marker position={coord}
                                icon={p.photo_url ? epPhotoIcon(p.photo_url, p.color) : epIcon(p.glyph, p.color)}
                                data-testid={`ep-pin-${p.id}-${zip}`}>
                          <Popup>
                            <div className="text-sm">
                              <div className="font-bold flex items-center gap-1"><span>{p.glyph}</span> <span className="capitalize">{p.name}</span></div>
                              {p.company && <div className="text-gray-600 text-xs capitalize">{p.company}</div>}
                              <div className="mt-1"><strong>Zip:</strong> {zip || "—"}</div>
                              <div><strong>All zips:</strong> {(p.territory_zips || []).join(", ") || "—"}</div>
                              {p.lifetime_spend > 0 && (
                                <div className="mt-1 text-emerald-600">
                                  <strong>Lifetime spend:</strong> ${p.lifetime_spend.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                  {" · "}{p.transaction_count || 0} txns
                                </div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                );
              })}

              {/* Client pins */}
              {clients.filter(c => c.lat && c.lng).map(c => (
                <CircleMarker key={c.id} center={[c.lat, c.lng]} radius={5}
                              pathOptions={{ color: "#FF3B30", fillOpacity: 0.9 }}>
                  <Popup>
                    <div className="text-sm font-medium capitalize">{c.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{c.company}</div>
                  </Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
        </Card>

        <Card className="p-5">
          <p className="label-eyebrow mb-3">Legend</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2" data-testid="legend-warehouse">
              <span className="inline-flex w-7 h-7 items-center justify-center rounded-full bg-slate-900 text-white text-sm border-2 border-yellow-400">🏭</span>
              <div>
                <div className="font-semibold">Warehouse</div>
                <div className="text-xs text-gray-500">Clinton, MD 20735</div>
              </div>
            </li>
            {epsWithGlyph.map((p) => (
              <li key={p.id} className="flex items-start gap-2" data-testid={`legend-partner-${p.id}`}>
                {p.photo_url ? (
                  <span className="inline-flex w-8 h-8 rounded-full overflow-hidden border-2 border-white flex-shrink-0"
                        style={{ boxShadow: `0 0 0 2px ${p.color}` }}>
                    <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover"
                         onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  </span>
                ) : (
                  <span className="inline-flex w-7 h-7 items-center justify-center rounded-full text-sm border-2 border-white flex-shrink-0"
                        style={{ background: p.color, boxShadow: `0 0 0 2px ${p.color}` }}>{p.glyph}</span>
                )}
                <div>
                  <div className="font-semibold capitalize">{p.name}</div>
                  <div className="text-xs text-gray-500">Zips {(p.territory_zips || []).join(", ") || "—"}</div>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
